import React, { useEffect, useMemo, useState } from 'react';
import { Wallet } from 'lucide-react';
import CustomButton from '../../../../components/ui/buttons/CustomButton';
import { camelizeKeys } from '../../../../utils/camelizeKeys';
import { cn } from '../../../../utils/classnames';
import SimpleToggle from '../../../../components/ui/toggles/SimpleToggle';
import { useToast } from '../../../../components/ui/toast/ToastContext';
import CustomSelect from '../../../../components/ui/selects/CustomSelect';
import { snakecaseKeys } from '../../../../utils/snakecaseKeys';
import { fetchWithCsrf } from '../../../../utils/fetchWithCsrf';
import AddWalletAssetModal from '../../../../components/modals/walletConfigs/AddWalletAssetModal';
import AddOffshoreWalletAssetModal from '../../../../components/modals/walletConfigs/AddOffshoreWalletAssetModal';
import ReplaceWalletAssetModal from '../../../../components/modals/walletConfigs/ReplaceWalletAssetModal';
import AddComplementaryAssetModal from '../../../../components/modals/walletConfigs/AddComplementaryAssetModal';
import { useConfirm } from '../../../../components/ui/confirm/ConfirmContext';
import WalletEditor from './WalletEditor';
import OffshoreWalletEditor from './OffshoreWalletEditor';

const getEmptyWalletForm = (isOffshore = false) => {
  if (isOffshore) {
    return {
      name: '',
      walletProfileId: null,
      walletType: 'investment',
      offshoreWalletAssetsAttributes: [],
      accountBalancePercentage: 0,
      maxVolatility: '',
      enabled: true,
    };
  }

  return {
    name: '',
    walletProfileId: null,
    walletType: '',
    walletAssetsAttributes: [],
    fixedIncomePercentage: 0,
    maxVolatility: '',
  };
};

const buildQuery = (params) =>
  Object.entries(params)
    .filter(([, v]) => v !== '' && v !== null && v !== undefined)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

const normalizeErrorMessage = (data, fallback) => {
  if (data?.errors?.length) return data.errors.join('\n');
  if (data?.error) return data.error;
  if (data?.message) return data.message;

  return fallback;
};

const normalizeNationalAsset = (newAssetDetails) => ({
  assetId: newAssetDetails.assetId,
  assetType: newAssetDetails.assetType,
  assetKind: newAssetDetails.assetKind,

  assetName: newAssetDetails.assetName,
  assetDisplayName:
    newAssetDetails.assetDisplayName ||
    newAssetDetails.assetName ||
    newAssetDetails.displayName ||
    newAssetDetails.name ||
    '',

  assetCnpj: newAssetDetails.assetCnpj || newAssetDetails.cnpj || '',
  assetCge: newAssetDetails.assetCge || newAssetDetails.cge || '',
  assetTicker: newAssetDetails.assetTicker || newAssetDetails.ticker || '',
  assetTSector: newAssetDetails.assetTSector || newAssetDetails.tSector || '',

  allocationPercentage: newAssetDetails.allocationPercentage || 0,
  _destroy: false,

  walletAssetComplementsAttributes: [],
  complementaryAssets: [],
});

const normalizeOffshoreAsset = (newAssetDetails, allocationPercentage = null) => ({
  assetId: newAssetDetails.assetId,
  assetType: newAssetDetails.assetType || 'OffshoreSymbol',

  assetName: newAssetDetails.assetName || newAssetDetails.name || '',
  assetDisplayName:
    newAssetDetails.assetDisplayName ||
    newAssetDetails.displayName ||
    newAssetDetails.assetName ||
    newAssetDetails.name ||
    '',

  assetAlias: newAssetDetails.assetAlias || newAssetDetails.alias || '',
  assetTicker: newAssetDetails.assetTicker || newAssetDetails.ticker || '',
  assetSymbolId: newAssetDetails.assetSymbolId || newAssetDetails.symbolId || '',
  assetSymbolType: newAssetDetails.assetSymbolType || newAssetDetails.symbolType || '',

  assetCurrency: newAssetDetails.assetCurrency || newAssetDetails.currency || '',
  assetExchange: newAssetDetails.assetExchange || newAssetDetails.exchange || '',
  assetCountry: newAssetDetails.assetCountry || newAssetDetails.country || '',
  assetProvider: newAssetDetails.assetProvider || newAssetDetails.provider || '',

  assetCategory: newAssetDetails.assetCategory || newAssetDetails.category || '',
  assetStrategy: newAssetDetails.assetStrategy || newAssetDetails.strategy || '',
  assetSector: newAssetDetails.assetSector || newAssetDetails.sector || '',
  assetKind: newAssetDetails.assetKind || newAssetDetails.kind || '',
  assetSubtype: newAssetDetails.assetSubtype || newAssetDetails.subtype || '',
  assetStatus: newAssetDetails.assetStatus || newAssetDetails.status || '',

  allocationPercentage:
    allocationPercentage !== null && allocationPercentage !== undefined
      ? allocationPercentage
      : newAssetDetails.allocationPercentage || 0,

  _destroy: false,
});

const Wallets = () => {
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [walletProfileOptions, setWalletProfileOptions] = useState([]);
  const [selectedWalletProfile, setSelectedWalletProfile] = useState(null);
  const [selectedWalletType, setSelectedWalletType] = useState('INVESTIMENTO');
  const [loading, setLoading] = useState(false);
  const [currentWallet, setCurrentWallet] = useState(null);
  const [selectedWalletShore, setSelectedWalletShore] = useState('NACIONAL');

  const isOffshore = selectedWalletShore === 'INTERNACIONAL';

  const [currentWalletForm, setCurrentWalletForm] = useState(() => getEmptyWalletForm(isOffshore));
  const [showAddWalletAssetModal, setShowAddWalletAssetModal] = useState(false);
  const [replaceConfig, setReplaceConfig] = useState(null);
  const [addComplementConfig, setAddComplementConfig] = useState(null);
  const [highlightId, setHighlightId] = useState(null);

  const { addToast } = useToast();

  // Marca `item._highlightId = newId` (mutação direta do objeto recém-criado, antes de
  // entrar no state) para disparar o scroll-to-center + destaque em WalletAssets/
  // OffshoreWalletAssets, e agenda a limpeza do marcador depois que o fade (2000ms) termina.
  const triggerHighlight = (fieldName, newId) => {
    setHighlightId(newId);

    setTimeout(() => {
      setCurrentWalletForm((prev) => ({
        ...prev,
        [fieldName]: (prev[fieldName] || []).map((item) => {
          if (item._highlightId === newId) {
            const cleared = { ...item };
            delete cleared._highlightId;
            return cleared;
          }

          if (item.complementaryAssets?.some((comp) => comp._highlightId === newId)) {
            return {
              ...item,
              complementaryAssets: item.complementaryAssets.map((comp) => {
                if (comp._highlightId !== newId) return comp;
                const clearedComp = { ...comp };
                delete clearedComp._highlightId;
                return clearedComp;
              }),
            };
          }

          return item;
        }),
      }));

      setHighlightId((current) => (current === newId ? null : current));
    }, 2200);
  };
  const confirm = useConfirm();

  const walletBaseUrl = isOffshore
    ? '/app/analyst/wallet_configs/offshore_wallets'
    : '/app/analyst/wallet_configs/wallets';

  const payloadRoot = isOffshore ? 'offshoreWallet' : 'wallet';

  const fetchWalletDetails = async (walletId) => {
    setLoading(true);

    try {
      const res = await fetch(`${walletBaseUrl}/${walletId}`);
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(normalizeErrorMessage(data, 'Erro ao carregar detalhes'));
      }

      const wallet = camelizeKeys(data).wallet;

      setCurrentWallet(wallet);

      if (isOffshore) {
        setCurrentWalletForm({
          name: wallet.name || '',
          walletProfileId: wallet.walletProfileId,
          walletType: 'investment',
          offshoreWalletAssetsAttributes: wallet.offshoreWalletAssetsAttributes || [],
          accountBalancePercentage: wallet.accountBalancePercentage || 0,
          maxVolatility: wallet.maxVolatility || '',
          enabled: wallet.enabled !== false,
        });
      } else {
        setCurrentWalletForm({
          name: wallet.name || '',
          walletProfileId: wallet.walletProfileId,
          walletType: wallet.walletType || '',
          walletAssetsAttributes: wallet.walletAssetsAttributes || [],
          fixedIncomePercentage: wallet.fixedIncomePercentage || 0,
          maxVolatility: wallet.maxVolatility || '',
        });
      }
    } catch (err) {
      addToast({
        type: 'error',
        message: err.message || 'Erro ao carregar detalhes',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (profileId, type, targetWalletId = null) => {
    setLoading(true);

    const query = buildQuery({
      walletProfile: profileId,
      walletType: isOffshore ? 'INVESTIMENTO' : type,
    });

    const url = query ? `${walletBaseUrl}?${query}` : walletBaseUrl;

    try {
      const res = await fetch(url);
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(normalizeErrorMessage(data, 'Erro ao carregar dados'));
      }

      const camelData = camelizeKeys(data);

      setWallets(camelData.wallets || []);

      if (targetWalletId) {
        setSelectedWallet(targetWalletId);
      } else {
        setSelectedWallet(camelData.wallets?.[0]?.id || null);
      }

      setWalletProfileOptions(camelData.walletProfiles || []);

      if (!profileId && camelData.walletProfiles?.length > 0) {
        setSelectedWalletProfile(camelData.walletProfiles[0].id);
      }
    } catch (err) {
      console.error('Erro ao carregar dados', err);

      addToast({
        type: 'error',
        message: err.message || 'Erro ao carregar dados',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setWallets([]);
    setSelectedWallet(null);
    setCurrentWallet(null);
    setCurrentWalletForm(getEmptyWalletForm(isOffshore));
    setSelectedWalletType('INVESTIMENTO');
    setShowAddWalletAssetModal(false);
    setReplaceConfig(null);
    setAddComplementConfig(null);
  }, [selectedWalletShore]);

  useEffect(() => {
    fetchData(selectedWalletProfile, selectedWalletType);
  }, [selectedWalletProfile, selectedWalletType, selectedWalletShore]);

  useEffect(() => {
    if (selectedWallet) {
      fetchWalletDetails(selectedWallet);
    } else {
      setCurrentWallet(null);
      setCurrentWalletForm(getEmptyWalletForm(isOffshore));
    }
  }, [selectedWallet, selectedWalletShore]);

  const handleChangeWalletShore = (nextShore) => {
    setSelectedWalletShore(nextShore);
    setSelectedWalletType('INVESTIMENTO');
  };

  const handleAddAssetToForm = (newAssetDetails) => {
    if (isOffshore) {
      if (replaceConfig) {
        handleReplaceOffshoreAssetInForm(newAssetDetails);
        return;
      }

      const normalizedAsset = normalizeOffshoreAsset(newAssetDetails);
      const newHighlightId = Date.now();
      normalizedAsset._highlightId = newHighlightId;

      setCurrentWalletForm((prev) => ({
        ...prev,
        offshoreWalletAssetsAttributes: [...(prev.offshoreWalletAssetsAttributes || []), normalizedAsset],
      }));

      setShowAddWalletAssetModal(false);
      triggerHighlight('offshoreWalletAssetsAttributes', newHighlightId);
      return;
    }

    const normalizedAsset = normalizeNationalAsset(newAssetDetails);
    const newHighlightId = Date.now();
    normalizedAsset._highlightId = newHighlightId;

    setCurrentWalletForm((prev) => ({
      ...prev,
      walletAssetsAttributes: [...(prev.walletAssetsAttributes || []), normalizedAsset],
    }));

    setShowAddWalletAssetModal(false);
    triggerHighlight('walletAssetsAttributes', newHighlightId);
  };

  const handleRemoveAssetFromForm = (indexToRemove) => {
    const fieldName = isOffshore ? 'offshoreWalletAssetsAttributes' : 'walletAssetsAttributes';

    setCurrentWalletForm((prev) => {
      const newAttributes = [...(prev[fieldName] || [])];
      const asset = newAttributes[indexToRemove];

      if (!asset) return prev;

      if (asset.id) {
        newAttributes[indexToRemove] = { ...asset, _destroy: true };
      } else {
        newAttributes.splice(indexToRemove, 1);
      }

      return { ...prev, [fieldName]: newAttributes };
    });
  };

  const handleAddComplementaryAssetToForm = (newComplementDetails) => {
    if (isOffshore) return;

    const newHighlightId = Date.now();

    setCurrentWalletForm((prev) => {
      const newAttributes = [...(prev.walletAssetsAttributes || [])];
      const parentIndex = addComplementConfig.index;

      const parentAsset = { ...newAttributes[parentIndex] };

      const complementForRails = {
        complementId: newComplementDetails.assetId,
        complementType: newComplementDetails.assetType,
      };

      const complementForReact = {
        id: `temp-${newHighlightId}`,
        name: newComplementDetails.assetName,
        cnpj: newComplementDetails.assetCnpj,
        cge: newComplementDetails.assetCge,
        displayName: newComplementDetails.assetName,
        _highlightId: newHighlightId,
      };

      parentAsset.walletAssetComplementsAttributes = [
        ...(parentAsset.walletAssetComplementsAttributes || []),
        complementForRails,
      ];

      parentAsset.complementaryAssets = [...(parentAsset.complementaryAssets || []), complementForReact];

      newAttributes[parentIndex] = parentAsset;

      return { ...prev, walletAssetsAttributes: newAttributes };
    });

    setAddComplementConfig(null);
    triggerHighlight('walletAssetsAttributes', newHighlightId);
  };

  const handleReplaceAssetInForm = (newAssetDetails) => {
    if (isOffshore || !replaceConfig) return;

    const newHighlightId = Date.now();

    setCurrentWalletForm((prev) => {
      const newAttributes = [...(prev.walletAssetsAttributes || [])];
      const oldAsset = newAttributes[replaceConfig.index];

      if (!oldAsset) return prev;

      if (oldAsset.id) {
        newAttributes[replaceConfig.index] = { ...oldAsset, _destroy: true };
      } else {
        newAttributes.splice(replaceConfig.index, 1);
      }

      const oldAssetAsComplementForRails = {
        complementId: oldAsset.assetId,
        complementType: oldAsset.assetType,
      };

      const oldAssetAsComplementForReact = {
        id: `temp-${newHighlightId}`,
        name: oldAsset.assetName,
        displayName: oldAsset.assetName,
        cnpj: oldAsset.assetCnpj,
        cge: oldAsset.assetCge,
      };

      const existingComplementsForRails = (oldAsset.walletAssetComplementsAttributes || [])
        .filter((comp) => !comp._destroy)
        .map((comp) => ({
          complementId: comp.complementId,
          complementType: comp.complementType,
        }));

      const replacementAsset = {
        assetId: newAssetDetails.assetId,
        assetType: newAssetDetails.assetType,
        assetKind: newAssetDetails.assetKind,
        assetName: newAssetDetails.assetName,
        assetDisplayName: newAssetDetails.assetName,
        assetCnpj: newAssetDetails.assetCnpj,
        assetCge: newAssetDetails.assetCge,
        allocationPercentage: oldAsset.allocationPercentage || 0,
        _destroy: false,
        _highlightId: newHighlightId,

        walletAssetComplementsAttributes: [...existingComplementsForRails, oldAssetAsComplementForRails],

        complementaryAssets: [...(oldAsset.complementaryAssets || []), oldAssetAsComplementForReact],
      };

      return {
        ...prev,
        walletAssetsAttributes: [...newAttributes, replacementAsset],
      };
    });

    setReplaceConfig(null);
    triggerHighlight('walletAssetsAttributes', newHighlightId);
  };

  const handleReplaceOffshoreAssetInForm = (newAssetDetails) => {
    if (!replaceConfig) return;

    const newHighlightId = Date.now();

    setCurrentWalletForm((prev) => {
      const newAttributes = [...(prev.offshoreWalletAssetsAttributes || [])];
      const oldAsset = newAttributes[replaceConfig.index];

      if (!oldAsset) return prev;

      if (oldAsset.id) {
        newAttributes[replaceConfig.index] = { ...oldAsset, _destroy: true };
      } else {
        newAttributes.splice(replaceConfig.index, 1);
      }

      const replacementAsset = normalizeOffshoreAsset(newAssetDetails, oldAsset.allocationPercentage || 0);
      replacementAsset._highlightId = newHighlightId;

      return {
        ...prev,
        offshoreWalletAssetsAttributes: [...newAttributes, replacementAsset],
      };
    });

    setReplaceConfig(null);
    setShowAddWalletAssetModal(false);
    triggerHighlight('offshoreWalletAssetsAttributes', newHighlightId);
  };

  const handleRemoveComplementaryAsset = (parentIndex, complementIndex) => {
    if (isOffshore) return;

    setCurrentWalletForm((prev) => {
      const newAttributes = [...(prev.walletAssetsAttributes || [])];
      const parentAsset = { ...newAttributes[parentIndex] };

      const newComplementsForRails = [...(parentAsset.walletAssetComplementsAttributes || [])];
      const newComplementsForReact = [...(parentAsset.complementaryAssets || [])];

      const compToRemoverForRails = newComplementsForRails[complementIndex];
      const compToRemoveForReact = newComplementsForReact[complementIndex];

      if (compToRemoverForRails && compToRemoverForRails.id) {
        newComplementsForRails[complementIndex] = {
          ...compToRemoverForRails,
          _destroy: true,
        };
      } else {
        newComplementsForRails.splice(complementIndex, 1);
      }

      if (compToRemoveForReact && compToRemoveForReact.id && !String(compToRemoveForReact.id).startsWith('temp-')) {
        newComplementsForReact[complementIndex] = {
          ...compToRemoveForReact,
          _destroy: true,
        };
      } else {
        newComplementsForReact.splice(complementIndex, 1);
      }

      parentAsset.walletAssetComplementsAttributes = newComplementsForRails;
      parentAsset.complementaryAssets = newComplementsForReact;
      newAttributes[parentIndex] = parentAsset;

      return { ...prev, walletAssetsAttributes: newAttributes };
    });
  };

  const handleSaveWallet = async () => {
    if (!currentWallet?.id) return;

    setLoading(true);

    const form = isOffshore
      ? {
          ...currentWalletForm,
          walletType: 'investment',
        }
      : currentWalletForm;

    const payload = snakecaseKeys({ [payloadRoot]: form });

    try {
      const res = await fetchWithCsrf(`${walletBaseUrl}/${currentWallet.id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(normalizeErrorMessage(data, 'Erro ao salvar edições'));
      }

      addToast({
        type: 'success',
        message: isOffshore ? 'Carteira offshore atualizada com sucesso!' : 'Carteira atualizada com sucesso!',
      });

      await fetchData(selectedWalletProfile, selectedWalletType, currentWallet.id);
      await fetchWalletDetails(currentWallet.id);
    } catch (err) {
      addToast({
        type: 'error',
        message: err.message || 'Erro ao salvar edições',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImportPolicy = async (file) => {
    if (!currentWallet?.id) {
      addToast({
        type: 'error',
        message: 'Nenhuma carteira selecionada para importar a política.',
      });

      return null;
    }

    if (!file) {
      addToast({
        type: 'error',
        message: 'Selecione um arquivo para importar.',
      });

      return null;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetchWithCsrf(`${walletBaseUrl}/${currentWallet.id}/import_policy`, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(normalizeErrorMessage(data, 'Erro ao importar estratégia de investimento.'));
      }

      addToast({
        type: 'success',
        message: data.message || 'Estratégia importada com sucesso!',
      });

      await fetchData(selectedWalletProfile, selectedWalletType, currentWallet.id);
      await fetchWalletDetails(currentWallet.id);

      return data;
    } catch (err) {
      addToast({
        type: 'error',
        message: err.message || 'Erro ao importar estratégia de investimento.',
      });

      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteImportPolicy = async () => {
    if (!currentWallet?.id) {
      addToast({
        type: 'error',
        message: 'Carteira não encontrada.',
      });

      return null;
    }

    setLoading(true);

    try {
      const res = await fetchWithCsrf(`${walletBaseUrl}/${currentWallet.id}/import_policy`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(normalizeErrorMessage(data, 'Erro ao excluir histórico da carteira.'));
      }

      addToast({
        type: 'success',
        message: data.message || 'Histórico da carteira excluído com sucesso.',
      });

      await fetchData(selectedWalletProfile, selectedWalletType, currentWallet.id);
      await fetchWalletDetails(currentWallet.id);

      return data;
    } catch (err) {
      addToast({
        type: 'error',
        message: err.message || 'Erro ao excluir histórico da carteira.',
      });

      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleAddWallet = async () => {
    setLoading(true);

    const form = isOffshore
      ? {
          walletProfileId: selectedWalletProfile,
          walletType: 'investment',
          name: 'Nova Carteira Offshore',
          accountBalancePercentage: 0,
          enabled: true,
        }
      : {
          walletProfileId: selectedWalletProfile,
          walletType: selectedWalletType,
          name: 'Nova Carteira',
        };

    const payload = snakecaseKeys({
      [payloadRoot]: form,
    });

    try {
      const res = await fetchWithCsrf(walletBaseUrl, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        throw new Error(
          normalizeErrorMessage(json, isOffshore ? 'Erro ao adicionar carteira offshore' : 'Erro ao adicionar carteira')
        );
      }

      addToast({
        type: 'success',
        message: isOffshore ? 'Carteira offshore adicionada com sucesso!' : 'Carteira adicionada com sucesso!',
      });

      const newWalletId = json.wallet?.id;

      await fetchData(selectedWalletProfile, selectedWalletType, newWalletId);
    } catch (err) {
      addToast({
        type: 'error',
        message: err.message,
      });

      await fetchData(selectedWalletProfile, selectedWalletType);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWallet = async () => {
    if (!currentWallet?.id) return;

    const ok = await confirm({
      title: isOffshore ? 'EXCLUIR CARTEIRA OFFSHORE' : 'EXCLUIR CARTEIRA',
      description: `Deseja realmente excluir a carteira "${currentWallet.name}"?`,
      confirmText: 'SIM, EXCLUIR',
      cancelText: 'CANCELAR',
    });

    if (!ok) return;

    setLoading(true);

    try {
      const res = await fetchWithCsrf(`${walletBaseUrl}/${currentWallet.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        throw new Error(normalizeErrorMessage(data, 'Erro ao excluir carteira.'));
      }

      addToast({
        type: 'success',
        message: data.message || 'Carteira excluída com sucesso!',
      });

      setCurrentWallet(null);
      setSelectedWallet(null);

      await fetchData(selectedWalletProfile, selectedWalletType);
    } catch (err) {
      console.log('Erro ao excluir carteira:', err);

      addToast({
        type: 'error',
        message: err.message || 'Erro ao excluir carteira.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateWallet = async () => {
    if (!currentWallet?.id) return;

    const ok = await confirm({
      title: isOffshore ? 'DUPLICAR CARTEIRA OFFSHORE' : 'DUPLICAR CARTEIRA',
      description: `Deseja criar uma cópia exata da carteira "${currentWallet.name}"?`,
      confirmText: 'SIM, DUPLICAR',
      cancelText: 'CANCELAR',
    });

    if (!ok) return;

    setLoading(true);

    const form = isOffshore
      ? {
          name: `Cópia de ${currentWalletForm.name}`,
          walletProfileId: currentWalletForm.walletProfileId,
          walletType: 'investment',
          accountBalancePercentage: currentWalletForm.accountBalancePercentage,
          maxVolatility: currentWalletForm.maxVolatility,
          enabled: currentWalletForm.enabled !== false,
          offshoreWalletAssetsAttributes: (currentWalletForm.offshoreWalletAssetsAttributes || [])
            .filter((asset) => !asset._destroy)
            .map((asset) => ({
              assetId: asset.assetId,
              assetType: asset.assetType || 'OffshoreSymbol',
              allocationPercentage: asset.allocationPercentage,
            })),
        }
      : {
          name: `Cópia de ${currentWalletForm.name}`,
          walletProfileId: currentWalletForm.walletProfileId,
          walletType: currentWalletForm.walletType,
          fixedIncomePercentage: currentWalletForm.fixedIncomePercentage,
          maxVolatility: currentWalletForm.maxVolatility,
          walletAssetsAttributes: (currentWalletForm.walletAssetsAttributes || [])
            .filter((asset) => !asset._destroy)
            .map((asset) => ({
              assetId: asset.assetId,
              assetType: asset.assetType,
              allocationPercentage: asset.allocationPercentage,
              walletAssetComplementsAttributes: (asset.walletAssetComplementsAttributes || [])
                .filter((complement) => !complement._destroy)
                .map((complement) => ({
                  complementId: complement.complementId,
                  complementType: complement.complementType,
                })),
            })),
        };

    const payload = snakecaseKeys({
      [payloadRoot]: form,
    });

    try {
      const res = await fetchWithCsrf(walletBaseUrl, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        throw new Error(normalizeErrorMessage(json, 'Erro ao duplicar carteira'));
      }

      addToast({
        type: 'success',
        message: isOffshore ? 'Carteira offshore duplicada com sucesso!' : 'Carteira duplicada com sucesso!',
      });

      const newWalletId = json.wallet?.id;

      await fetchData(selectedWalletProfile, selectedWalletType, newWalletId);
    } catch (err) {
      addToast({
        type: 'error',
        message: err.message || 'Erro ao duplicar carteira',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setCurrentWalletForm((prev) => {
      const updatedForm = { ...prev, [name]: value };

      if (isOffshore) {
        updatedForm.walletType = 'investment';
        return updatedForm;
      }

      if (name === 'walletType' && value !== 'investment') {
        updatedForm.fixedIncomePercentage = 0;
      }

      return updatedForm;
    });
  };

  const handleChangeAssetAllocation = (indexToUpdate, newValue) => {
    const fieldName = isOffshore ? 'offshoreWalletAssetsAttributes' : 'walletAssetsAttributes';

    setCurrentWalletForm((prev) => {
      const newAttributes = [...(prev[fieldName] || [])];

      if (!newAttributes[indexToUpdate]) return prev;

      newAttributes[indexToUpdate] = {
        ...newAttributes[indexToUpdate],
        allocationPercentage: newValue == null ? 0 : newValue,
      };

      return { ...prev, [fieldName]: newAttributes };
    });
  };

  const handleChangeFixedIncome = (newValue) => {
    setCurrentWalletForm((prev) => ({
      ...prev,
      fixedIncomePercentage: newValue == null ? 0 : newValue,
    }));
  };

  const handleChangeAccountBalance = (newValue) => {
    setCurrentWalletForm((prev) => ({
      ...prev,
      accountBalancePercentage: newValue == null ? 0 : newValue,
    }));
  };

  const totalAllocation = useMemo(() => {
    if (!currentWalletForm) return 0;

    if (isOffshore) {
      const assetsSum = (currentWalletForm.offshoreWalletAssetsAttributes || [])
        .filter((asset) => !asset._destroy)
        .reduce((sum, asset) => sum + (parseFloat(asset.allocationPercentage) || 0), 0);

      const accountBalance = parseFloat(currentWalletForm.accountBalancePercentage) || 0;

      return assetsSum + accountBalance;
    }

    const assetsSum = (currentWalletForm.walletAssetsAttributes || [])
      .filter((asset) => !asset._destroy)
      .reduce((sum, asset) => sum + (parseFloat(asset.allocationPercentage) || 0), 0);

    const fixedIncome = parseFloat(currentWalletForm.fixedIncomePercentage) || 0;

    return assetsSum + fixedIncome;
  }, [currentWalletForm, isOffshore]);

  const isTotalComplete = Math.abs(totalAllocation - 100) < 0.01;

  const handleOpenAddAssetModal = () => {
    setReplaceConfig(null);
    setShowAddWalletAssetModal(true);
  };

  const handleOpenReplaceAssetModal = (asset, originalIndex) => {
    setReplaceConfig({ asset, index: originalIndex });

    if (isOffshore) {
      setShowAddWalletAssetModal(true);
    }
  };

  const walletEditorProps = {
    currentWallet,
    currentWalletForm,
    walletProfileOptions,
    totalAllocation,
    isTotalComplete,
    loading,
    highlightId,
    onChangeField: handleChange,
    onChangeAssetAllocation: handleChangeAssetAllocation,
    onChangeFixedIncome: handleChangeFixedIncome,
    onRemoveAsset: handleRemoveAssetFromForm,
    onReplaceAsset: handleOpenReplaceAssetModal,
    onRemoveComplementaryAsset: handleRemoveComplementaryAsset,
    onAddComplementaryAsset: (asset, originalIndex) => setAddComplementConfig({ asset, index: originalIndex }),
    onAddAsset: handleOpenAddAssetModal,
    onDiscardChanges: () => currentWallet?.id && fetchWalletDetails(currentWallet.id),
    onSave: handleSaveWallet,
    onDuplicate: handleDuplicateWallet,
    onDelete: handleDeleteWallet,
    onImportPolicy: handleImportPolicy,
    onDeleteImportPolicy: handleDeleteImportPolicy,
    walletShore: selectedWalletShore,
  };

  const offshoreWalletEditorProps = {
    currentWallet,
    currentWalletForm,
    walletProfileOptions,
    totalAllocation,
    isTotalComplete,
    loading,
    highlightId,
    onChangeField: handleChange,
    onChangeAssetAllocation: handleChangeAssetAllocation,
    onChangeAccountBalance: handleChangeAccountBalance,
    onRemoveAsset: handleRemoveAssetFromForm,
    onReplaceAsset: handleOpenReplaceAssetModal,
    onAddAsset: handleOpenAddAssetModal,
    onDiscardChanges: () => currentWallet?.id && fetchWalletDetails(currentWallet.id),
    onSave: handleSaveWallet,
    onDuplicate: handleDuplicateWallet,
    onDelete: handleDeleteWallet,
    onImportPolicy: handleImportPolicy,
    onDeleteImportPolicy: handleDeleteImportPolicy,
    walletShore: selectedWalletShore,
  };

  return (
    <div className="p-0 flex flex-col flex-1 min-h-0">
      <div className="grid gap-5 h-full" style={{ gridTemplateColumns: '400px minmax(0, 1fr)' }}>
        {/* Coluna 1 */}
        <div className="bg-white p-7 rounded-custom20 gap-5 flex flex-col h-full min-h-0">
          <span className="flex items-center gap-2">
            <Wallet className="text-tealMedium" size={20} strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold text-blueDarker">Carteiras de Investimento</h1>
          </span>

          <SimpleToggle
            options={['NACIONAL', 'INTERNACIONAL']}
            selected={selectedWalletShore}
            onChange={handleChangeWalletShore}
          />

          <div
            className="bg-blueLight p-4 text-blueDarker font-normal flex flex-col gap-1"
            style={{ borderRadius: '10px' }}
          >
            <CustomSelect
              name="wallet_profile"
              label="Filtrar por Perfil de Carteira"
              options={walletProfileOptions.map((profile) => ({
                label: profile.name,
                value: profile.id,
                colorHex: profile.colorHex,
              }))}
              value={selectedWalletProfile}
              onChange={(e) => setSelectedWalletProfile(e.target.value)}
              disabled={walletProfileOptions.length === 0 || loading}
            />

            <div className="mt-3">
              <SimpleToggle
                options={isOffshore ? ['INVESTIMENTO'] : ['INVESTIMENTO', 'PREVIDÊNCIA']}
                selected={selectedWalletType}
                onChange={setSelectedWalletType}
              />
            </div>
          </div>

          <div className="space-y-4 flex-1 pr-2 min-h-0 custom-scrollbar overflow-y-auto">
            {wallets.map((wallet) => (
              <div
                key={wallet.id}
                className={cn(
                  'flex flex-row gap-2 items-center p-4 bg-blueLighter rounded-lg',
                  selectedWallet === wallet.id ? 'bg-blueLight' : 'bg-transparent hover:bg-blueUltraLight',
                  'cursor-pointer'
                )}
                onClick={() => setSelectedWallet(wallet.id)}
                style={{ borderRadius: '10px' }}
              >
                <h2 className="text-lg font-semibold text-blueDarker">{wallet.name}</h2>
              </div>
            ))}
          </div>

          <div>
            <CustomButton
              variant="outline"
              size="md"
              className="w-full"
              onClick={handleAddWallet}
              disabled={loading || !selectedWalletProfile}
            >
              + ADICIONAR CARTEIRA
            </CustomButton>
          </div>
        </div>

        {/* COLUNA 2 */}
        <div className="bg-white py-7 pl-7 pr-5 rounded-custom20 flex flex-col h-full overflow-y-auto custom-scrollbar">
          {currentWallet ? (
            <>
              {!isOffshore && <WalletEditor {...walletEditorProps} />}
              {isOffshore && <OffshoreWalletEditor {...offshoreWalletEditorProps} />}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center opacity-80">
              <div className="bg-grayExtraLight2 p-5 rounded-full mb-4">
                <Wallet className="text-blueSteel" size={48} strokeWidth={1.2} />
              </div>

              <h2 className="text-xl font-semibold text-blueDarker">Nenhuma carteira selecionada</h2>

              <p className="text-base text-blueSteel mt-2 max-w-xs">
                Selecione uma carteira ou crie uma nova para visualizar e editar seus detalhes.
              </p>
            </div>
          )}
        </div>
      </div>

      {showAddWalletAssetModal && !isOffshore && (
        <AddWalletAssetModal
          onClose={() => setShowAddWalletAssetModal(false)}
          onSuccess={handleAddAssetToForm}
          walletType={currentWalletForm.walletType}
        />
      )}

      {showAddWalletAssetModal && isOffshore && (
        <AddOffshoreWalletAssetModal
          onClose={() => {
            setShowAddWalletAssetModal(false);
            setReplaceConfig(null);
          }}
          onSuccess={handleAddAssetToForm}
          assetToReplace={replaceConfig?.asset || null}
          mode={replaceConfig ? 'replace' : 'add'}
        />
      )}

      {replaceConfig && !isOffshore && (
        <ReplaceWalletAssetModal
          assetToReplace={replaceConfig.asset}
          onClose={() => setReplaceConfig(null)}
          onSuccess={handleReplaceAssetInForm}
        />
      )}

      {addComplementConfig && !isOffshore && (
        <AddComplementaryAssetModal
          parentAsset={addComplementConfig.asset}
          onClose={() => setAddComplementConfig(null)}
          onSuccess={handleAddComplementaryAssetToForm}
        />
      )}
    </div>
  );
};

export default Wallets;
